/**
 * Shown to a signed-in user whose account has `status='disabled'`.
 *
 * Disabled users are either previously-denied account requests or
 * previously-active users a super admin has since disabled. Either
 * way, they can't use the app. The copy is matter-of-fact (no
 * apology, no "sorry for the inconvenience") and offers a sign-out
 * button in case they want to try a different account.
 */

import { useAuth } from "@/features/auth/useAuth";
import { useLogout } from "@/features/auth/useLogout";

export function DisabledPage() {
  const { user } = useAuth();
  const logout = useLogout();

  return (
    <main className="max-w-[640px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Account disabled
        </div>
        <h1 className="font-serif text-display-md text-ink">
          This account is <em className="italic font-normal text-coral-deep">disabled</em>.
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          The super admin has disabled this account. If you think this was a
          mistake, contact the person who runs this instance of Make It Count.
        </p>
      </div>

      {user && (
        <div className="rounded-md border border-edge bg-surface px-5 py-4 max-w-[560px]">
          <div className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted mb-1">
            Signed in as
          </div>
          <div className="font-sans text-[15px] font-medium text-ink">
            {user.display_name}
          </div>
          <div className="font-mono text-[13px] text-ink-soft mt-[2px]">{user.email}</div>
        </div>
      )}

      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="self-start px-5 py-2 rounded-md font-sans text-[14px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
      >
        {logout.isPending ? "Signing out…" : "Sign out"}
      </button>
    </main>
  );
}
